// packages/decision-ui/src/components/DiscoveryHub/PhysicsGate.tsx

import React from 'react';

export interface PhysicsGateProps {
  mainsPressure: number;
  flowRate: number;
  combiViable: boolean;
  unventedViable: boolean;
  onChange: (mainsPressure: number, flowRate: number) => void;
}

const PhysicsGate: React.FC<PhysicsGateProps> = ({
  mainsPressure,
  flowRate,
  combiViable,
  unventedViable,
  onChange,
}) => {
  const handleMainsPressureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    onChange(value, flowRate);
  };

  const handleFlowRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    onChange(mainsPressure, value);
  };

  const getStatusColor = (viable: boolean) => viable ? '#28a745' : '#dc3545';
  const getStatusText = (viable: boolean) => viable ? 'Viable' : 'Not Viable';

  return (
    <div style={{
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      padding: '1.5rem',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    }}>
      <h3 style={{ 
        fontSize: '1.25rem', 
        fontWeight: 600, 
        marginTop: 0,
        marginBottom: '0.5rem',
      }}>
        🌊 Physics Gate
      </h3>
      <p style={{ 
        fontSize: '0.875rem', 
        color: '#6c757d', 
        marginBottom: '1.5rem',
      }}>
        High inductive value - Water supply determines what's physically possible
      </p>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '1.5rem',
        marginBottom: '1.5rem',
      }}>
        {/* Mains Pressure Dial */}
        <div>
          <label style={{ 
            display: 'block', 
            fontWeight: 600, 
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
          }}>
            Mains Pressure (bar)
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="range"
              min="0.5"
              max="4.0"
              step="0.1"
              value={mainsPressure}
              onChange={handleMainsPressureChange}
              style={{
                width: '100%',
                height: '8px',
                borderRadius: '4px',
                background: mainsPressure >= 1.0 
                  ? 'linear-gradient(to right, #28a745 0%, #28a745 100%)' 
                  : 'linear-gradient(to right, #dc3545 0%, #dc3545 100%)',
                outline: 'none',
                WebkitAppearance: 'none',
              }}
            />
            <div style={{ 
              textAlign: 'center', 
              fontSize: '1.5rem', 
              fontWeight: 700,
              marginTop: '0.5rem',
              color: mainsPressure >= 1.0 ? '#28a745' : '#dc3545',
            }}>
              {mainsPressure.toFixed(1)} bar
            </div>
          </div>
          {mainsPressure < 1.0 && (
            <div style={{ 
              fontSize: '0.75rem', 
              color: '#dc3545', 
              marginTop: '0.5rem',
              fontWeight: 500,
            }}>
              ⚠️ Below minimum threshold (1.0 bar)
            </div>
          )}
        </div>

        {/* Flow Rate Dial */}
        <div>
          <label style={{ 
            display: 'block', 
            fontWeight: 600, 
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
          }}>
            Flow Rate (L/min)
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="range"
              min="5"
              max="30"
              step="0.5"
              value={flowRate}
              onChange={handleFlowRateChange}
              style={{
                width: '100%',
                height: '8px',
                borderRadius: '4px',
                background: flowRate >= 12 
                  ? 'linear-gradient(to right, #28a745 0%, #28a745 100%)' 
                  : 'linear-gradient(to right, #dc3545 0%, #dc3545 100%)',
                outline: 'none',
                WebkitAppearance: 'none',
              }}
            />
            <div style={{ 
              textAlign: 'center', 
              fontSize: '1.5rem', 
              fontWeight: 700,
              marginTop: '0.5rem',
              color: flowRate >= 12 ? '#28a745' : '#dc3545',
            }}>
              {flowRate.toFixed(1)} L/min
            </div>
          </div>
          {flowRate < 12 && (
            <div style={{ 
              fontSize: '0.75rem', 
              color: '#dc3545', 
              marginTop: '0.5rem',
              fontWeight: 500,
            }}>
              ⚠️ Below minimum threshold (12 L/min)
            </div>
          )}
        </div>
      </div>

      {/* System Viability Cards */}
      <div style={{
        borderTop: '1px solid #e9ecef',
        paddingTop: '1.5rem',
      }}>
        <h4 style={{ 
          fontSize: '0.875rem', 
          fontWeight: 600, 
          marginBottom: '1rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: '#495057',
        }}>
          System Viability
        </h4>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '1rem',
        }}>
          {/* Combi Boiler Card */}
          <div style={{
            padding: '1rem',
            borderRadius: '6px',
            backgroundColor: combiViable ? '#d4edda' : '#f8d7da',
            border: `2px solid ${getStatusColor(combiViable)}`,
            opacity: combiViable ? 1 : 0.5,
            transition: 'all 0.3s ease',
            transform: combiViable ? 'scale(1)' : 'scale(0.95)',
          }}>
            <div style={{ 
              fontWeight: 600, 
              marginBottom: '0.5rem',
              color: getStatusColor(combiViable),
            }}>
              Combi Boiler
            </div>
            <div style={{ 
              fontSize: '0.75rem', 
              color: getStatusColor(combiViable),
              fontWeight: 500,
            }}>
              {getStatusText(combiViable)}
            </div>
          </div>

          {/* Unvented System Card */}
          <div style={{
            padding: '1rem',
            borderRadius: '6px',
            backgroundColor: unventedViable ? '#d4edda' : '#f8d7da',
            border: `2px solid ${getStatusColor(unventedViable)}`,
            opacity: unventedViable ? 1 : 0.5,
            transition: 'all 0.3s ease',
            transform: unventedViable ? 'scale(1)' : 'scale(0.95)',
          }}>
            <div style={{ 
              fontWeight: 600, 
              marginBottom: '0.5rem',
              color: getStatusColor(unventedViable),
            }}>
              Unvented System
            </div>
            <div style={{ 
              fontSize: '0.75rem', 
              color: getStatusColor(unventedViable),
              fontWeight: 500,
            }}>
              {getStatusText(unventedViable)}
            </div>
          </div>
        </div>
      </div>

      {/* Informational Note */}
      {(!combiViable || !unventedViable) && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '6px',
          fontSize: '0.875rem',
          color: '#856404',
        }}>
          <strong>Physical Reality:</strong> When mains pressure or flow rate is insufficient, 
          certain system types become physically impossible. This isn't a preference - 
          it's the laws of hydraulics at work.
        </div>
      )}
    </div>
  );
};

export default PhysicsGate;
